import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

export default class extends Controller {
  static targets = ["container", "data"]
  static values = { type: String }

  connect() {
    this.renderChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  renderChart() {
    try {
      const chartData = JSON.parse(this.dataTarget.innerHTML)
      if (!chartData || chartData.length === 0) return

      const type = this.typeValue || "bar"
      let options

      if (type === "line") {
        options = {
          series: [{ name: "Ventes", data: chartData.map(d => d.total) }],
          chart: {
            type: "line",
            height: 250,
            toolbar: { show: false },
            zoom: { enabled: false }
          },
          xaxis: {
            categories: chartData.map(d => this.formatDate(d.date)),
            tooltip: { enabled: false }
          },
          yaxis: {
            labels: { formatter: (v) => `${v.toFixed(0)} €` }
          },
          colors: ["#3b82f6"],
          stroke: { width: 2, curve: "smooth" },
          fill: {
            type: "gradient",
            gradient: {
              shadeIntensity: 1,
              opacityFrom: 0.4,
              opacityTo: 0.05,
              stops: [0, 95, 100]
            }
          },
          dataLabels: { enabled: false },
          tooltip: {
            y: { formatter: (v) => `${v.toFixed(2)} €` }
          },
          grid: {
            borderColor: "#f3f4f6",
            strokeDashArray: 4
          }
        }
      } else if (type === "bar") {
        options = {
          series: [{ name: "Total", data: chartData.map(d => d.total) }],
          chart: {
            type: "bar",
            height: chartData.length * 42 + 40,
            toolbar: { show: false }
          },
          plotOptions: {
            bar: {
              horizontal: true,
              barHeight: "65%",
              distributed: true,
              dataLabels: { position: "bottom" }
            }
          },
          xaxis: {
            categories: chartData.map(d => d.name),
            labels: {
              formatter: (v) => `${v.toFixed(0)} €`
            }
          },
          yaxis: {
            labels: {
              formatter: (val) => {
                const d = chartData.find(c => c.name === val)
                return d ? `${d.name} (${d.count ?? 0})` : val
              },
              style: { fontWeight: "700" },
              maxWidth: 250
            }
          },
          colors: chartData.map(d => d.color || "#3b82f6"),
          dataLabels: {
            enabled: true,
            formatter: (v) => `${v.toFixed(0)} €`,
            style: { fontSize: "11px", colors: ["#374151"] }
          },
          legend: { show: false },
          tooltip: {
            theme: "light",
            fillSeriesSize: false,
            custom: ({ series, seriesIndex, dataPointIndex }) => {
              const d = chartData[dataPointIndex]
              const total = series[seriesIndex][dataPointIndex]
              const count = d.count || 0
              return `
                <div class="p-2 min-w-[180px]">
                  <div class="font-bold text-base mb-2" style="color: ${d.color || '#374151'}">${d.name}</div>
                  <div class="flex justify-between items-center text-sm" style="gap: 16px;">
                    <span class="text-gray-500">Nombre de ventes</span>
                    <span class="font-bold text-gray-800">${count}</span>
                  </div>
                  <div class="flex justify-between items-center text-sm mt-1" style="gap: 16px;">
                    <span class="text-gray-500">Total</span>
                    <span class="font-bold text-gray-800">${total.toFixed(2)} €</span>
                  </div>
                </div>`
            }
          },
          grid: {
            borderColor: "#f3f4f6",
            strokeDashArray: 4
          }
        }
      }

      this.chart = new ApexCharts(this.containerTarget, options)
      this.chart.render()
    } catch (e) {
      console.error("Erreur lors du rendu du graphique :", e)
    }
  }

  formatDate(dateStr) {
    const d = new Date(dateStr)
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })
  }
}